import type { AvailableCountries } from '@models/locale.enum';

import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AVAILABLE_COUNTRIES } from '@models/locale.enum';
import { Store } from '@ngrx/store';
import { selectCurrentCountry } from '@stores/router.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'hlp-select-country',
  imports: [MatIcon, NgClass],
  template: `<div class="container tw:z-10">
    <div class="current-country">
      <mat-icon class="tw:rounded-full" [svgIcon]="country() + '-rounded'" />
    </div>

    @if (countrySelectorOpened()) {
      <div class="tw:flex country-select tw:z-10">
        @for (country of countriesAvailable(); track country) {
          <button class="country-select__btn" (click)="selectCountry(country)" type="button">
            <mat-icon [svgIcon]="country + '-rounded'" />
          </button>
        }
      </div>
    }

    <button
      class="arrow-btn"
      [ngClass]="{ 'arrow-btn--opened': countrySelectorOpened() }"
      (click)="countrySelectorOpened.set(!countrySelectorOpened())"
      type="button"
    >
      <mat-icon svgIcon="angle-right-b" />
    </button>
  </div>`,
  styles: [
    `
      .container {
        position: absolute;
        left: calc(50% - 30px);
        bottom: 70px;
        background-color: white;
        border-radius: 4.25rem;
        padding: 0.25rem;
        color: var(--color-primary);
        display: flex;
        align-items: center;
        box-shadow: 0 5px 14px 0 var(--color-black-1);
      }

      .current-country {
        display: flex;
        border: 2px solid transparent;
      }

      .arrow-btn {
        display: flex;
        border: none;
        padding: 0;
        transition: transform 0.3s;

        &--opened {
          transform: rotate(180deg);
        }
      }

      .country-select {
        margin-left: 0.25rem;

        &__btn {
          display: flex;
          padding: 0;
          border: 2px solid transparent;
          border-radius: 50%;

          &:hover {
            border: 2px solid var(--color-grey-8);
            cursor: pointer;
          }
        }
      }
    `,
  ],
})
export class SelectCountryComponent {
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly country = this.#store.selectSignal(selectCurrentCountry);

  readonly #allCountries = AVAILABLE_COUNTRIES;

  readonly countriesAvailable = computed(() => this.#allCountries.filter((country) => country !== this.country()));
  readonly countrySelectorOpened = signal(false);

  selectCountry(country: AvailableCountries) {
    const segments = this.#router.url.split('/').filter((segment) => segment);
    segments[0] = country;

    window.open(`/${segments.join('/')}`, '_self');
  }
}
